import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Paper,
  Chip,
  Grid,
  Collapse,
  useTheme,
  alpha
} from '@mui/material';
import {
  ExpandMore,
  ExpandLess,
  TableRestaurant,
  Receipt,
  AttachMoney,
  History
} from '@mui/icons-material';
import DateRangeSelector, { DateRange, getDateRangeForPreset } from './DateRangeSelector';
import FloorSelector from './FloorSelector';
import TableCard, { TableCardSkeleton } from './TableCard';
import ExportButton from './ExportButton';
import ExportProgressModal from './ExportProgressModal';
import { useFloorSummary } from '../../hooks/useFloorSummary';
import { useExportJob } from '../../hooks/useExportJob';
import { formatCurrency } from '../../api/invoiceApi';
import { AppIconButton } from '../ui';

interface TableOrdersTabProps {
  onDateRangeChange?: (range: DateRange) => void;
  initialDateRange?: DateRange;
}

const TableOrdersTab: React.FC<TableOrdersTabProps> = ({
  onDateRangeChange,
  initialDateRange
}) => {
  const theme = useTheme();
  const [exportModalOpen, setExportModalOpen] = useState(false);

  // Use the floor summary hook for data management
  const {
    floors,
    tablesByFloor,
    selectedFloorId,
    expandedFloors,
    loadingFloors,
    loadingTables,
    error,
    dateRange,
    setDateRange,
    selectFloor,
    toggleFloorExpansion,
    fetchTablesForFloor
  } = useFloorSummary({
    initialDateRange: initialDateRange || getDateRangeForPreset('today')
  });

  // Use the export job hook
  const {
    currentJob,
    recentJobs,
    isExporting,
    isLoading: isExportLoading,
    error: exportError,
    start: startExport,
    cancel: cancelExport,
    downloadFile
  } = useExportJob({
    onComplete: () => {
      // Optionally show a success notification
    },
    onError: (err) => {
      console.error('Export error:', err);
    }
  });

  const handleStartExport = () => {
    startExport(dateRange.startDate, dateRange.endDate);
  };

  // Notify parent of date range changes
  useEffect(() => {
    onDateRangeChange?.(dateRange);
  }, [dateRange, onDateRangeChange]);

  const handleDateRangeChange = (newRange: DateRange) => {
    setDateRange(newRange);
  };

  const handleFloorSelect = (floorId: number | null) => {
    selectFloor(floorId);
    // When a specific floor is selected, expand it automatically
    if (floorId !== null && !expandedFloors.has(floorId)) {
      toggleFloorExpansion(floorId);
    }
  };

  const handleTableClick = (tableId: number) => {
    // TODO: Navigate to session timeline view (Phase 4)
    console.log('Table clicked:', tableId);
  };

  // Filter floors based on selection
  const displayedFloors = selectedFloorId === null
    ? floors
    : floors.filter(f => f.id === selectedFloorId);

  if (error) {
    return (
      <Box sx={{ p: 3, textAlign: 'center', color: 'error.main' }}>
        <Typography>{error}</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Date Range Selector and Export */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <DateRangeSelector
          value={dateRange}
          onChange={handleDateRangeChange}
        />
        <Box sx={{ flex: 1 }} />
        <ExportButton
          currentJob={currentJob}
          isExporting={isExporting}
          isLoading={isExportLoading}
          error={exportError}
          onStartExport={handleStartExport}
          onDownload={downloadFile}
          size="small"
        />
        {(isExporting || currentJob) && (
          <AppIconButton
            icon={<History fontSize="small" />}
            tooltip="View export history"
            variant="ghost"
            size="small"
            onClick={() => setExportModalOpen(true)}
            sx={{ ml: -1 }}
          />
        )}
        <Typography variant="body2" color="text.secondary">
          {floors.length} floor{floors.length !== 1 ? 's' : ''} found
        </Typography>
      </Box>

      {/* Export Progress Modal */}
      <ExportProgressModal
        open={exportModalOpen}
        onClose={() => setExportModalOpen(false)}
        currentJob={currentJob}
        recentJobs={recentJobs}
        isExporting={isExporting}
        onCancel={cancelExport}
        onDownload={downloadFile}
      />

      {/* Floor Selector (Horizontal Scrollable) */}
      <FloorSelector
        floors={floors}
        selectedFloorId={selectedFloorId}
        onFloorSelect={handleFloorSelect}
        loading={loadingFloors}
      />

      {/* Loading State */}
      {loadingFloors ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : floors.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">
            No floors configured. Set up your venue layout first.
          </Typography>
        </Paper>
      ) : (
        /* Floors List with Expandable Tables */
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {displayedFloors.map((floor) => (
            <Paper
              key={floor.id}
              sx={{
                overflow: 'hidden',
                borderRadius: 2,
                border: `1px solid ${theme.palette.divider}`
              }}
            >
              {/* Floor Header */}
              <Box
                onClick={() => toggleFloorExpansion(floor.id)}
                sx={{
                  p: 2,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  backgroundColor: expandedFloors.has(floor.id)
                    ? alpha(theme.palette.primary.main, 0.05)
                    : 'transparent',
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.action.hover, 0.8)
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {floor.name}
                  </Typography>
                  {floor.floorNumber !== null && (
                    <Chip
                      label={`Floor ${floor.floorNumber}`}
                      size="small"
                      variant="outlined"
                    />
                  )}
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                  {/* Floor Statistics */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TableRestaurant sx={{ fontSize: 18, color: 'text.secondary' }} />
                    <Typography variant="body2" color="text.secondary">
                      {floor.tableCount} tables
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Receipt sx={{ fontSize: 18, color: 'text.secondary' }} />
                    <Typography variant="body2" color="text.secondary">
                      {floor.totalSessionCount} sessions
                    </Typography>
                  </Box>

                  {floor.activeSessionCount > 0 && (
                    <Chip
                      label={`${floor.activeSessionCount} active`}
                      size="small"
                      color="success"
                    />
                  )}

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AttachMoney sx={{ fontSize: 18, color: 'success.main' }} />
                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'success.main' }}>
                      {formatCurrency(floor.totalRevenue)}
                    </Typography>
                  </Box>

                  <AppIconButton
                    icon={expandedFloors.has(floor.id) ? <ExpandLess /> : <ExpandMore />}
                    tooltip={expandedFloors.has(floor.id) ? 'Collapse' : 'Expand'}
                    variant="ghost"
                    size="small"
                  />
                </Box>
              </Box>

              {/* Tables Grid - Expanded */}
              <Collapse in={expandedFloors.has(floor.id)}>
                <Box sx={{ p: 2, backgroundColor: theme.palette.background.default }}>
                  {loadingTables[floor.id] ? (
                    /* Loading Skeleton */
                    <Grid container spacing={2}>
                      {[1, 2, 3, 4].map((i) => (
                        <Grid item xs={12} sm={6} md={4} lg={3} key={i}>
                          <TableCardSkeleton />
                        </Grid>
                      ))}
                    </Grid>
                  ) : tablesByFloor[floor.id]?.length === 0 ? (
                    <Typography color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                      No tables on this floor
                    </Typography>
                  ) : (
                    <Grid container spacing={2}>
                      {tablesByFloor[floor.id]?.map((table) => (
                        <Grid item xs={12} sm={6} md={4} lg={3} key={table.id}>
                          <TableCard
                            table={table}
                            onClick={handleTableClick}
                            dateRange={dateRange}
                            expandable={true}
                          />
                        </Grid>
                      ))}
                    </Grid>
                  )}
                </Box>
              </Collapse>
            </Paper>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default TableOrdersTab;
